import styled from '@emotion/native';
import { MessageTemplate } from '@quromedical/models';
import { Col, Row } from 'components/base';
import { CrudForm, SubmissionHandler } from 'components/form';
import { Field, SelectOption } from 'components/types';
import { Text, fadeColor, Spacer, Visible, Divider } from 'design-system';
import { templateFetchers } from 'integration/formFetchers';
import React, { useCallback, useState } from 'react';
import { Modal, Pressable, useWindowDimensions } from 'react-native';
import { strings } from 'strings';

interface Layout {
  height: number;
  width: number;
}

type FormData = {
  template: SelectOption<MessageTemplateFetcher | undefined, string>;
};

export type MessageTemplateFetcher = Omit<MessageTemplate, 'effectivePeriod'>;
interface BottomSheetProps {
  layout: Layout;
  patientId: string;
  openBottomSheet: boolean;
  toggleBottomSheet: () => void;
  setMultiLine: (multiLine: boolean) => void;
  openFilePicker: (open: boolean) => void;
  templateMessage: (value: MessageTemplateFetcher | undefined) => void;
  hideAttachmentIfSessionExpired: boolean;
}

export const ChatBottomSheet: React.FC<BottomSheetProps> = ({
  layout,
  openBottomSheet,
  toggleBottomSheet,
  templateMessage,
  openFilePicker,
  setMultiLine,
  hideAttachmentIfSessionExpired = false,
}) => {
  const { height, width } = useWindowDimensions();
  const [showTemplates, setShowTemplates] = useState(false);
  const [showFilePicker, setShowFilePicker] = useState(false);

  const BackgroundWrapper = styled.View(({ theme }) => ({
    position: 'absolute',
    left: width - layout.width,
    bottom: 0,
    height: height,
    width: layout.width,
    backgroundColor: fadeColor(theme.color['base-grey'], 0.8),
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
  }));

  const Wrapper = styled.View(({ theme }) => ({
    backgroundColor: theme.color['base-grey'],
    width: layout.width,
    bottom: 0,
    right: 0,
    position: 'absolute',
    borderColor: theme.color.divider,
    borderWidth: 0.5,
  }));

  const showGridPicker = !showTemplates && !showFilePicker;

  const reset = useCallback(() => {
    setShowTemplates(false);
    setShowFilePicker(false);
    toggleBottomSheet();
  }, [toggleBottomSheet]);

  const createField = (): Field<FormData>[] => [
    {
      heading: strings.HeadingReasonOfAdmission,
      subfields: [
        {
          key: 'template',
          type: 'combobox-single',
          label: strings.CommunicationSelectTemplates,
          fetcher: templateFetchers,
          searchable: true,
          returnFullOption: true,
        },
      ],
    },
  ];
  const fields = createField();

  const onSubmit = useCallback<SubmissionHandler<FormData>>(
    (formData: FormData) => {
      templateMessage(formData.template.meta);
      setMultiLine(true);
      reset();

      return {};
    },
    [reset, setMultiLine, templateMessage]
  );

  const onCancel = useCallback(() => {
    templateMessage(undefined);
    setMultiLine(false);
    reset();

    return {};
  }, [reset, setMultiLine, templateMessage]);

  const openFile = useCallback(() => {
    openFilePicker(true);
    setShowFilePicker(true);
    reset();
    return {};
  }, [openFilePicker, reset]);

  return (
    <Modal animationType="slide" transparent={true} visible={openBottomSheet}>
      <BackgroundWrapper>
        <Wrapper>
          {/* This is set to false for now because we are not showing you the option to select media  */}
          <Visible if={showGridPicker}>
            <Col>
              <Visible if={!hideAttachmentIfSessionExpired}>
                <Pressable onPress={openFile}>
                  <Row alignItems="center" justifyContent="space-around">
                    <Text variant="subheading" style={{ padding: 16 }}>
                      Attachment
                    </Text>
                  </Row>
                </Pressable>
              </Visible>
              <Divider orientation="horizontal" />
              <Pressable onPress={() => setShowTemplates(true)}>
                <Row alignItems="center" justifyContent="space-around">
                  <Text variant="subheading" style={{ padding: 16 }}>
                    Templates
                  </Text>
                </Row>
              </Pressable>
              <Divider orientation="horizontal" />

              <Pressable onPress={() => reset()}>
                <Row alignItems="center" justifyContent="space-around">
                  <Text color="primary" variant="subheading" style={{ padding: 16 }}>
                    Cancel
                  </Text>
                </Row>
              </Pressable>
            </Col>
          </Visible>
          <Visible if={showTemplates}>
            <Text variant="subheading">Select Templates</Text>
            <Spacer height={20} />
            <CrudForm
              key={'template'}
              fields={fields}
              initialValues={{}}
              showSubmitButton={true}
              onSubmit={onSubmit}
              showSecondarySubmitButton={true}
              onSecondarySubmit={onCancel}
            />
          </Visible>
        </Wrapper>
      </BackgroundWrapper>
    </Modal>
  );
};
